/**
 * Granular Permission System
 *
 * Permissions follow the format: resource:action
 * Examples: clients:view, clients:create, clients:edit, clients:delete
 */

// Permission action types
export type PermissionAction = 'view' | 'create' | 'edit' | 'delete' | 'manage';

// All available permissions grouped by category
export const PERMISSION_CATEGORIES = {
  clients: {
    label: 'Clients',
    description: 'Access to client management',
    permissions: {
      'clients:view': 'View assigned clients only',
      'clients:view_all': 'View all clients (bypasses team/producer restrictions)',
      'clients:create': 'Create new clients',
      'clients:edit': 'Edit existing clients',
      'clients:delete': 'Delete clients',
    },
  },
  prospects: {
    label: 'Prospects',
    description: 'Access to prospect management',
    permissions: {
      'prospects:view': 'View assigned prospects only',
      'prospects:view_all': 'View all prospects (bypasses team/producer restrictions)',
      'prospects:create': 'Create new prospects',
      'prospects:edit': 'Edit existing prospects',
      'prospects:delete': 'Delete prospects',
    },
  },
  quotes: {
    label: 'Quoting',
    description: 'Access to quotes and proposals',
    permissions: {
      'quotes:view': 'View quotes and proposals',
      'quotes:create': 'Create new quotes',
      'quotes:edit': 'Edit existing quotes',
      'quotes:delete': 'Delete quotes',
    },
  },
  commissions: {
    label: 'Agent Commissions',
    description: 'Access to agent/employee commission views and statements',
    permissions: {
      'commissions:view': 'View commission statements',
      'commissions:reports': 'Generate commission reports',
      'commissions:manage': 'Manage commission settings',
    },
  },
  agencyHr: {
    label: 'Agency HR',
    description: "Manage the agency's own HR — time-off policies, requests and holidays for agency staff",
    permissions: {
      'agency_hr:view': "View the agency's HR tab (policies, requests, balances)",
      'agency_hr:manage': 'Manage agency time-off policies, approve/deny staff requests, adjust balances',
    },
  },
  agencyCommissions: {
    label: 'Agency Commissions',
    description: 'Access to agency commission management and statement generation',
    permissions: {
      'agency_commissions:view': 'View agency commissions',
      'agency_commissions:reports': 'Generate agency commission statements',
      'agency_commissions:manage': 'Manage agency commissions and rules',
    },
  },
  documents: {
    label: 'Documents',
    description: 'Access to document management',
    permissions: {
      'documents:view': 'View documents',
      'documents:upload': 'Upload documents',
      'documents:delete': 'Delete documents',
      'documents:folders': 'Manage folders',
    },
  },
  directory: {
    label: 'Directory',
    description: 'Access to contacts and carriers',
    permissions: {
      'directory:view': 'View directory contacts',
      'directory:create': 'Add new contacts',
      'directory:edit': 'Edit contacts',
      'directory:delete': 'Delete contacts',
    },
  },
  marketing: {
    label: 'Marketing',
    description: 'Access to marketing tools',
    permissions: {
      'marketing:view': 'View marketing campaigns',
      'marketing:create': 'Create campaigns',
      'marketing:edit': 'Edit campaigns',
      'marketing:manage': 'Manage marketing settings',
    },
  },
  forms: {
    label: 'Forms',
    description: 'Access to the form library, fields, and mappings',
    permissions: {
      'forms:view': 'View form library and form details',
      'forms:create': 'Create new forms',
      'forms:edit': 'Edit forms, fields, and mappings',
      'forms:delete': 'Delete forms',
    },
  },
  agency: {
    label: 'Agency Settings',
    description: 'Access to agency administration',
    permissions: {
      'agency:view': 'View agency settings',
      'agency:edit': 'Edit agency information',
      'agency:members': 'Manage agency members',
      'agency:roles': 'Manage roles and permissions',
      'agency:benefits': 'View and manage agency-level benefits',
      'agency:organization': 'View organization structure (read-only)',
    },
  },
  reports: {
    label: 'Reports',
    description: 'Access to reporting',
    permissions: {
      'reports:view': 'View basic reports',
      'reports:financial': 'View financial reports',
      'reports:export': 'Export reports',
    },
  },
  employees: {
    label: 'Employees',
    description: 'Access to employee management',
    permissions: {
      'employees:view': 'View employee list and details',
      'employees:create': 'Add new employees',
      'employees:edit': 'Edit employee information',
      'employees:delete': 'Remove employees',
    },
  },
  tasks: {
    label: 'Tasks',
    description: 'Access to task management',
    permissions: {
      'tasks:view': 'View tasks and tickets',
      'tasks:create': 'Create new tasks',
      'tasks:edit': 'Edit existing tasks',
      'tasks:delete': 'Delete tasks',
    },
  },
  plans: {
    label: 'Plans & Benefits',
    description: 'Access to plan management',
    permissions: {
      'plans:view': 'View plans and benefits',
      'plans:create': 'Create new plans',
      'plans:edit': 'Edit plan details',
      'plans:delete': 'Delete plans',
    },
  },
  enrollments: {
    label: 'Enrollments',
    description: 'Access to enrollment management',
    permissions: {
      'enrollments:view': 'View enrollments',
      'enrollments:create': 'Create new enrollments',
      'enrollments:edit': 'Edit enrollments',
      'enrollments:delete': 'Cancel enrollments',
    },
  },
  carriers: {
    label: 'Carriers',
    description: 'Access to carrier management',
    permissions: {
      'carriers:view': 'View carrier information',
      'carriers:create': 'Add new carriers',
      'carriers:edit': 'Edit carrier details',
      'carriers:delete': 'Remove carriers',
    },
  },
} as const;

// Flatten all permissions into a single array
export const ALL_PERMISSIONS = Object.values(PERMISSION_CATEGORIES).flatMap(
  (category) => Object.keys(category.permissions)
);

// Special permission that grants all access
export const FULL_ACCESS = 'full_access';

/**
 * Default permissions for the `employer` role — a client-scoped admin of their
 * OWN company. Employers are authorized server-side by role (tenant scoping),
 * and carry no materialized permissions, so this set exists purely to drive
 * UI visibility (PermissionButton, etc.) in the employer portal.
 * Excludes broker/agency-only domains: prospects, quotes, commissions, agency
 * settings, carriers, directory, marketing, forms. Plans are view-only (brokers
 * configure plan details).
 */
export const EMPLOYER_DEFAULT_PERMISSIONS: string[] = [
  'clients:view', 'clients:edit',
  'employees:view', 'employees:create', 'employees:edit', 'employees:delete',
  'enrollments:view', 'enrollments:create', 'enrollments:edit', 'enrollments:delete',
  // Employers may EDIT their OWN client's plans (not create/delete — that's the
  // broker's job). The server scopes plan edits to plan.clientId === their client
  // (see requirePermissionOrEmployerOwnsPlan).
  'plans:view', 'plans:edit',
  'documents:view', 'documents:upload', 'documents:delete', 'documents:folders',
  'reports:view', 'reports:export',
  'tasks:view', 'tasks:create', 'tasks:edit', 'tasks:delete',
];

// Legacy permission mapping (for backward compatibility)
export const LEGACY_PERMISSION_MAP: Record<string, string[]> = {
  'Full Access': [FULL_ACCESS],
  'User Management': ['agency:members', 'agency:roles'],
  'Financial Reports': ['reports:financial', 'commissions:reports'],
  'System Settings': ['agency:edit', 'agency:roles'],
  'Client Management': ['clients:view', 'clients:create', 'clients:edit', 'clients:delete'],
  'Quote Generation': ['quotes:view', 'quotes:create', 'quotes:edit'],
  'Policy Management': ['clients:edit', 'documents:view', 'documents:upload'],
  'Commission Reports': ['commissions:view', 'commissions:reports'],
  'Basic Reports': ['reports:view'],
  'Client Support': ['clients:view', 'documents:view'],
  'Basic Data Entry': ['clients:view', 'clients:edit', 'documents:upload'],
  'View Reports': ['reports:view'],
};

/**
 * Check if a user has a specific permission
 * @param userPermissions - Array of permissions the user has
 * @param requiredPermission - The permission to check for
 * @returns boolean
 */
export function hasPermission(userPermissions: string[], requiredPermission: string): boolean {
  // Full access grants everything
  if (userPermissions.includes(FULL_ACCESS) || userPermissions.includes('Full Access')) {
    return true;
  }

  // Direct match
  if (userPermissions.includes(requiredPermission)) {
    return true;
  }

  // Check legacy permissions
  for (const legacyPerm of userPermissions) {
    const mappedPerms = LEGACY_PERMISSION_MAP[legacyPerm];
    if (mappedPerms && mappedPerms.includes(requiredPermission)) {
      return true;
    }
  }

  return false;
}

/**
 * Check if a user has any of the specified permissions
 * @param userPermissions - Array of permissions the user has
 * @param requiredPermissions - Array of permissions to check (ANY match = true)
 * @returns boolean
 */
export function hasAnyPermission(userPermissions: string[], requiredPermissions: string[]): boolean {
  return requiredPermissions.some(perm => hasPermission(userPermissions, perm));
}

/**
 * Check if a user has all of the specified permissions
 * @param userPermissions - Array of permissions the user has
 * @param requiredPermissions - Array of permissions to check (ALL must match)
 * @returns boolean
 */
export function hasAllPermissions(userPermissions: string[], requiredPermissions: string[]): boolean {
  return requiredPermissions.every(perm => hasPermission(userPermissions, perm));
}

/**
 * Get human-readable label for a permission
 */
export function getPermissionLabel(permission: string): string {
  for (const category of Object.values(PERMISSION_CATEGORIES)) {
    const perms = category.permissions as Record<string, string>;
    if (permission in perms) {
      return perms[permission];
    }
  }
  return permission;
}

// Type for permission keys
export type Permission = typeof ALL_PERMISSIONS[number] | typeof FULL_ACCESS;
